import { useState } from 'react';
import { Typography, Link, Stack, Alert, Button, TextField, Box } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { ArrowBack, CheckCircle, Mail } from '@mui/icons-material';
import AuthLayout from '../components/auth/AuthLayout';
import { useLang } from '../contexts/langContext';
import api from '../services/api';

function ForgotPasswordPage() {
  const { lang } = useLang();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const isEnglish = lang === 'en';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await api({ feedbackMode: 'off' }).post('/auth/forgot-password', {
        email: email.trim(),
        lang,
      });

      setMessage(response.data.message);
      setIsSuccess(true);
      setEmail(''); // Clear form on success
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 
        (isEnglish ? 'An error occurred. Please try again.' : '发生错误，请重试。');
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout title={isEnglish ? 'Reset Password' : '重置密码'}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
        {isEnglish 
          ? "Enter your email address and we'll send you a link to reset your password" 
          : "输入您的邮箱地址，我们将发送重置密码的链接给您"
        }
      </Typography>

      {isSuccess ? (
        <Alert 
          severity="success" 
          variant="outlined"
          icon={<Mail />}
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {isEnglish ? 'Email Sent Successfully' : '邮件发送成功'}
          </Typography>
          {message}
        </Alert>
      ) : (
        <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              type="email"
              label={isEnglish ? 'Email Address' : '邮箱地址'}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={isEnglish ? 'Enter your email address' : '请输入您的邮箱地址'}
              required
              disabled={isLoading}
              error={!!error}
            />

            {error && (
              <Alert severity="error" variant="outlined">
                {error}
              </Alert>
            )}

            {message && !isSuccess && (
              <Alert severity="info" variant="outlined">
                {message}
              </Alert>
            )}

            <Button 
              type="submit" 
              fullWidth 
              variant="contained" 
              size="large"
              disabled={isLoading || !email.trim()}
              sx={{ py: 1.5 }}
            >
              {isLoading ? (
                isEnglish ? 'Sending...' : '发送中...'
              ) : (
                isEnglish ? 'Send Reset Email' : '发送重置邮件'
              )}
            </Button>
          </Stack>
        </Box>
      )}
      
      <Stack sx={{ textAlign: 'center' }}>
        <Button
          component={RouterLink}
          to="/login"
          startIcon={<ArrowBack />}
          variant="text"
          sx={{ 
            color: 'text.secondary',
            '&:hover': {
              color: 'primary.main',
            }
          }}
        >
          {isEnglish ? 'Back to login' : '返回登录'}
        </Button>
      </Stack>
    </AuthLayout>
  );
}

export default ForgotPasswordPage; 